import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import { authService, LoginRequest, RegisterRequest } from '../services/auth';

interface LoginPageProps {
  onLoginSuccess: () => void;
  context?: string;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess, context }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Login form state
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerFullName, setRegisterFullName] = useState('');

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      const credentials: LoginRequest = {
        username: loginUsername,
        password: loginPassword,
      };

      await authService.login(credentials);
      
      
      setSuccess('Login successful!');
      
      // Wait a bit to show success message, then call onLoginSuccess
      setTimeout(() => {
        onLoginSuccess();
      }, 500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (registerPassword !== registerConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (registerPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const userData: RegisterRequest = {
        username: registerUsername,
        email: registerEmail,
        password: registerPassword,
        full_name: registerFullName || undefined,
      };

      await authService.register(userData);
      setSuccess('Registration successful! Redirecting to login...');
      
      // Clear register form
      setRegisterUsername('');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterConfirmPassword('');
      setRegisterFullName('');
      
      // Keep loading state active during redirect
      // Switch to login tab after showing success message
      setTimeout(() => {
        setActiveTab(0); // Switch to login tab
        setSuccess(null); // Clear success message
        setLoading(false); // Stop loading after redirect
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
      setLoading(false); // Stop loading on error
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
      }}
    >
      <Card sx={{ maxWidth: 450, width: '100%', m: 2, backgroundColor: '#1a1a1a', border: '1px solid #404040' }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ color: '#c0c0c0', fontWeight: 700 }}>
            Nina Software
          </Typography>

          {context && <Alert severity="info" sx={{mb:2}}>{context}</Alert>}
          <Tabs value={activeTab} onChange={handleTabChange} centered sx={{ mb: 3 }}>
            <Tab label="Login" />
            <Tab label="Register" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Login Form */}
          {activeTab === 0 && (
            <form onSubmit={handleLogin}>
              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                margin="normal"
                value={loginUsername}
                onChange={(e) => setLoginUsername(e.target.value)}
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                variant="outlined"
                margin="normal"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                required
                disabled={loading}
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </form>
          )}

          {/* Register Form */}
          {activeTab === 1 && (
            <form onSubmit={handleRegister}>
              <Alert severity="info" sx={{mb:2}}>Create your account and receive 100 complimentary credits to try Face and Body Mocap.</Alert>
              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                margin="normal"
                value={registerUsername}
                onChange={(e) => setRegisterUsername(e.target.value)}
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                variant="outlined"
                margin="normal"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                required
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Full Name (optional)"
                variant="outlined"
                margin="normal"
                value={registerFullName}
                onChange={(e) => setRegisterFullName(e.target.value)}
                disabled={loading}
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                variant="outlined"
                margin="normal"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                required
                disabled={loading}
                helperText="At least 6 characters"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                variant="outlined"
                margin="normal"
                value={registerConfirmPassword}
                onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Register'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
